<template>
  <section class="services">
    <div class="container">
      <h2 class="animate-fade-in">{{ $t('servicesSection.title') }}</h2>
      <RiceDivider :centered="true"/>
      <p class="description animate-slide-up">
        {{ $t('servicesSection.description') }}
      </p>

      <!-- Video Section -->
      <div class="videos-section animate-fade-in">
        <h3 class="videos-title">{{ $t('servicesSection.videosTitle') }}</h3>

        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Đang tải video...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <p>{{ error }}</p>
          <button class="retry-btn" @click="loadVideos">Thử lại</button>
        </div>

        <!-- Videos Grid -->
        <div v-else class="videos-grid">
          <div
              v-for="(video, index) in videos"
              :key="video.id"
              :class="{ 'animate-slide-in': true }"
              :style="{ animationDelay: `${index * 0.2}s` }"
              class="video-wrapper">

            <div class="video-container">
              <iframe
                  :src="getEmbedUrl(video.id)"
                  :title="video.name"
                  allowfullscreen
                  class="google-drive-video"
                  frameborder="0">
              </iframe>
            </div>
          </div>
        </div>

        <!-- No Videos State -->
        <div v-if="!loading && !error && videos.length === 0" class="no-videos">
          <i class="fas fa-video"></i>
          <p>Chưa có video nào trong thư mục</p>
        </div>
      </div>

      <!-- Call to Action Button -->
      <div class="cta-section animate-slide-up">
        <router-link class="services-btn" to="/services">
          <span>{{ $t('servicesSection.viewAllServices') }}</span>
          <i class="fas fa-arrow-right"></i>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import RiceDivider from './RiceDivider.vue';

export default {
  name: "Services",
  components: {
    RiceDivider
  },
  data() {
    return {
      videos: [],
      loading: false,
      error: null,
      // Google Drive API configuration
      GOOGLE_API_KEY: process.env.VUE_APP_GOOGLE_API_KEY,
      FOLDER_ID: '1Dv6iUwxB9tAeJbP9ZSjhexak_SiNRQ1h',
      // Video title mapping (optional - for custom titles)
      videoTitles: {
        // Example: 'video-filename.mp4': 'Custom Title'
      }
    }
  },
  async mounted() {
    await this.loadVideos();
  },
  methods: {
    async loadVideos() {
      this.loading = true;
      this.error = null;

      try {
        if (this.GOOGLE_API_KEY) {
          await this.loadVideosFromAPI();
        } else {
          throw new Error('Google API Key not found. Please check your .env file.');
        }
      } catch (err) {
        console.error('Error loading videos:', err);
        this.error = 'Không thể tải video. Vui lòng thử lại sau.';
      } finally {
        this.loading = false;
      }
    },

    async loadVideosFromAPI() {
      const url = `https://www.googleapis.com/drive/v3/files?q='${this.FOLDER_ID}'+in+parents+and+mimeType+contains+'video'&key=${this.GOOGLE_API_KEY}&fields=files(id,name,size,modifiedTime,mimeType)`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.videos = data.files || [];

      // Sort by modification time (newest first)
      this.videos.sort((a, b) => new Date(b.modifiedTime) - new Date(a.modifiedTime));
    },

    getEmbedUrl(fileId) {
      // Use embed URL with more parameters to hide controls and external links
      return `https://drive.google.com/file/d/${fileId}/preview?usp=sharing&controls=0&modestbranding=1&rel=0`;
    },

    formatVideoTitle(filename) {
      // Check if custom title exists
      if (this.videoTitles[filename]) {
        return this.videoTitles[filename];
      }

      // Auto-format filename to title
      return filename
          .replace(/\.[^/.]+$/, '') // Remove extension
          .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
          .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
    }
  }
};
</script>

<style scoped>
.services {
  padding: 80px 0;
  background-color: var(--white);
  position: relative;
  overflow: hidden;
}

.services::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle fill="rgba(139,119,85,0.03)" cx="20" cy="20" r="3"/><circle fill="rgba(139,119,85,0.025)" cx="80" cy="80" r="4"/><circle fill="rgba(139,119,85,0.02)" cx="50" cy="10" r="2"/><circle fill="rgba(139,119,85,0.03)" cx="10" cy="60" r="2"/></svg>');
  pointer-events: none;
  animation: subtleFloat 30s infinite ease-in-out;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

h2 {
  text-align: center;
  color: var(--primary-color);
  font-size: 2.5rem;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 300;
}

.description {
  text-align: center;
  max-width: 800px;
  margin: 30px auto 60px;
  line-height: 1.8;
  color: var(--text-color);
  font-size: 1.1rem;
}

.videos-section {
  margin: 60px 0;
  text-align: center;
}

.videos-title {
  font-size: 1.8rem;
  color: var(--primary-color);
  margin-bottom: 40px;
  font-weight: 300;
}

/* Loading, Error, No Videos States */
.loading-container,
.error-container,
.no-videos {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-color);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--divider-color);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.retry-btn {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 15px;
  transition: background 0.3s ease;
}

.retry-btn:hover {
  background: var(--secondary-color);
}

.no-videos i {
  font-size: 48px;
  color: var(--divider-color);
  margin-bottom: 20px;
}

.videos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  justify-items: center;
  margin-bottom: 50px;
}

.video-wrapper {
  background: white;
  overflow: hidden;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 300px;
  opacity: 0;
}

.video-wrapper.animate-slide-in {
  animation: slideInUp 0.6s ease-out forwards;
}

.video-wrapper:hover {
  transform: translateY(-8px);
  box-shadow: 8px 8px 0 0 #8b6b47;
}

.video-container {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 133.33%; /* 3:4 aspect ratio (height/width * 100) */
  overflow: hidden;
}

.google-drive-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* CTA Section */
.cta-section {
  text-align: center;
  margin-top: 60px;
}

.services-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: var(--primary-color);
  text-decoration: none;
  border: 1px solid var(--divider-color, var(--primary-color));
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 300;
  letter-spacing: 0.3px;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
}

.services-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #764908;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: -1;
}

.services-btn:hover::before {
  transform: scaleX(1);
}

.services-btn:hover {
  color: white;
  border-color: #764908;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(118, 73, 8, 0.2);
}

.services-btn i {
  transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-size: 0.85rem;
}

.services-btn:hover i {
  transform: translateX(3px);
}

/* Animations */
.animate-fade-in {
  animation: fadeIn 0.8s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.8s ease-out 0.2s both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes subtleFloat {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 0.6;
  }
  25% {
    transform: translate(8px, -8px) rotate(1deg);
    opacity: 0.8;
  }
  50% {
    transform: translate(-5px, 5px) rotate(-1deg);
    opacity: 0.7;
  }
  75% {
    transform: translate(5px, -3px) rotate(0.5deg);
    opacity: 0.9;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .services {
    padding: 60px 0;
  }

  h2 {
    font-size: 2rem;
    margin-bottom: 15px;
  }

  .description {
    font-size: 1rem;
    margin: 25px auto 50px;
  }

  .videos-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
  }

  .video-wrapper {
    max-width: 250px;
  }

  .video-container {
    padding-bottom: 133.33%; /* Keep 3:4 aspect ratio */
  }

  .services-btn {
    padding: 14px 28px;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .videos-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .video-wrapper {
    max-width: 200px;
  }

  .video-container {
    padding-bottom: 133.33%; /* Keep 3:4 aspect ratio */
  }
}
</style>